import { Badge, BlockStack, Button, Card, Checkbox, Grid, InlineStack, Modal, Page, Text, TextField, Toast } from "@shopify/polaris";
import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from 'react-redux'
import { createSettingAsync, getAllSetting, getSettingAsync } from "../redux/slice/commonSlice";
import { useAppFormik } from "../contexts/FormikContext";
import ContextSaveBar from "../components/ContextSaveBar";
import contentCopyImage from "../assets/image/contentCopyImage.png";
import devtollImage from "../assets/image/devtollImage.png";
import rightclickImage from "../assets/image/rightclick.png";
import keyboardImage from "../assets/image/keyboardImage.png";

const Placeholder = ({ height = 'auto' }) => {
    return (
        <div
            style={{
                height: height,
            }}
        />
    );
};

const SettingsPage = () => {
    const { isCreateSettingLoading } = useSelector(getAllSetting)
    const { values, setFieldValue, initialValues, resetForm } = useAppFormik();
    const dispatch = useDispatch();

    const [toastActive, setToastActive] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [previewModalActive, setPreviewModalActive] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    const showToast = useCallback((message) => {
        setToastMessage(message);
        setToastActive(true);
    }, []);

    const toastMarkup = toastActive ? <Toast content={toastMessage} onDismiss={() => setToastActive(false)} /> : null;

    // Helper function to mark changes when user interacts with fields
    const markAsChanged = () => {
        setHasUnsavedChanges(true);
    };

    const handleToggleProtection = (key) => {
        const current = values.copyPrevention[key];
        setFieldValue(`copyPrevention.${key}`, !current);
        markAsChanged();
    };

    const handleKeyboardToggle = (key) => {
        const current = values.keyboardProtection[key];
        setFieldValue(`keyboardProtection.${key}`, !current);
        markAsChanged();
    };

    const handleSave = async () => {
        // Sync blocked IP addresses from blockIp to blocks.blockedIpAddresses
        const updatedValues = {
            ...values,
            blocks: {
                ...values.blocks,
                blockedIpAddresses: values.blockIp?.blockedIpAddresses || []
            }
        };
        await dispatch(createSettingAsync({ payload: { settings: updatedValues }, callback: handleCallBack }));
    };

    const handleCallBack = () => {
        dispatch(getSettingAsync({}));
        showToast("Settings Saved Successfully!");
        setHasUnsavedChanges(false);
    };

    const handlePreview = () => {
        setPreviewModalActive(true);
    };

    const handleDiscard = () => {
        // Reset form to initial values using Formik's resetForm
        resetForm({ values: initialValues });
        setHasUnsavedChanges(false);
        showToast("Changes discarded");
    };

    return (
        <Page
            secondaryActions={[{ content: "Preview", onAction: handlePreview }]}
            title="Protection Setting"
        >
            <Grid>
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                        <Card>
                            <InlineStack align="space-between" blockAlign="center" gap='200'>
                                <InlineStack gap="300" blockAlign="center">
                                <img src={"https://cdn.shopify.com/s/files/1/0964/8878/4171/files/copyContentNew.png?v=1766824644"} alt="Content Copy Protection" style={{ width: '50px', height: '50px', objectFit: 'contain', borderRadius: '8px' }} />
                                <BlockStack gap="200">
                                        <InlineStack gap="200" blockAlign="center">
                                            <Text fontWeight="semibold">Content Copy Protection</Text>
                                            <Badge tone={values.copyPrevention.contentCopy ? "success" : "critical"}>
                                                {values.copyPrevention.contentCopy ? "Activated" : "Deactivated"}
                                            </Badge>
                                        </InlineStack>
                                    <Text tone="subdued" variant="bodySm" style={{ maxWidth: '160px', wordWrap: 'break-word', lineHeight: '1.4' }}>Prevent text copying.</Text>
                                    </BlockStack>
                                </InlineStack>
                                <Button tone={values.copyPrevention.contentCopy ? "critical" : "success"}
                                    onClick={() => handleToggleProtection("contentCopy")}>
                                    {values.copyPrevention.contentCopy ? "Disable" : "Enable"}
                                </Button>
                            </InlineStack>
                        <Placeholder height="10px" />
                            <BlockStack gap="400">
                            <Checkbox
                                label="Warning Message"
                                checked={values.showWarningMessage?.textCopy || false}
                                onChange={(val) => {
                                    setFieldValue(`showWarningMessage.textCopy`, val);
                                    markAsChanged();
                                }}
                            />
                            {values.showWarningMessage?.textCopy && (
                                <TextField
                                    label="Message for textCopy"
                                    value={values.warningMessages.textCopy}
                                    onChange={(val) => {
                                        setFieldValue(`warningMessages.textCopy`, val);
                                        markAsChanged();
                                    }}
                                    multiline={4}
                                    autoComplete="off"
                                    helpText="This message will be displayed when users attempt to copy content"
                                />
                            )}
                        </BlockStack>
                    </Card>
                </Grid.Cell>
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                    <Card>
                        <InlineStack align="space-between" blockAlign="center">
                            <InlineStack gap="300" blockAlign="center">
                                <img src="https://cdn.shopify.com/s/files/1/0964/8878/4171/files/rightClickNew.png?v=1766824476" alt="Right Click Protection" style={{ width: '50px', height: '50px', objectFit: 'contain', borderRadius: '8px' }} />
                                <BlockStack gap="200">
                                    <InlineStack gap="200">
                                        <Text fontWeight="semibold">Right Click Protection</Text>
                                        <Badge tone={values.copyPrevention.rightClick ? "success" : "critical"}>
                                            {values.copyPrevention.rightClick ? "Activated" : "Deactivated"}
                                        </Badge>
                                    </InlineStack>
                                    <Text tone="subdued" variant="bodySm" style={{ maxWidth: '160px', wordWrap: 'break-word', lineHeight: '1.4' }}>Disable right click menu.</Text>
                                </BlockStack>
                            </InlineStack>
                            <Button
                                tone={values.copyPrevention.rightClick ? "critical" : "success"}
                                onClick={() => handleToggleProtection("rightClick")}
                            >
                                {values.copyPrevention.rightClick ? "Disable" : "Enable"}
                            </Button>
                        </InlineStack>
                        <Placeholder height="10px" />
                        <BlockStack gap="400">
                            <Checkbox
                                label="Warning Message"
                                checked={values.showWarningMessage?.rightClick || false}
                                onChange={(val) => {
                                    setFieldValue(`showWarningMessage.rightClick`, val);
                                    markAsChanged();
                                }}
                            />
                            {values.showWarningMessage?.rightClick && (
                                <TextField
                                    label="Message for rightClick"
                                    value={values.warningMessages.rightClick}
                                    onChange={(val) => {
                                        setFieldValue(`warningMessages.rightClick`, val);
                                        markAsChanged();
                                    }}
                                    multiline={4}
                                    autoComplete="off"
                                    helpText="This message will be displayed when users attempt to right click"
                                />
                            )}
                            </BlockStack>
                        </Card>
                </Grid.Cell>
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                        <Card>
                            <InlineStack align="space-between" blockAlign="center">
                                <InlineStack gap="300" blockAlign="center">
                                <img src="https://cdn.shopify.com/s/files/1/0964/8878/4171/files/devtoolNew.png?v=1766824476" alt="DevTools Protection" style={{ width: '50px', height: '50px', objectFit: 'contain', borderRadius: '8px' }} />
                                    <BlockStack gap="200">
                                        <InlineStack gap="200">
                                            <Text fontWeight="semibold">DevTools Protection</Text>
                                            <Badge tone={values.copyPrevention.devTool ? "success" : "critical"}>
                                                {values.copyPrevention.devTool ? "Activated" : "Deactivated"}
                                            </Badge>
                                        </InlineStack>
                                    <Text tone="subdued" variant="bodySm" style={{ maxWidth: '160px', wordWrap: 'break-word', lineHeight: '1.4' }}>Prevent opening developer tools.</Text>
                                    </BlockStack>
                                </InlineStack>
                                <Button
                                    tone={values.copyPrevention.devTool ? "critical" : "success"}
                                    onClick={() => handleToggleProtection("devTool")}
                                >
                                    {values.copyPrevention.devTool ? "Disable" : "Enable"}
                                </Button>
                            </InlineStack>
                        <Placeholder height="10px" />
                        <BlockStack gap="400">
                            <Checkbox
                                label="Warning Message"
                                checked={values.showWarningMessage?.devTool || false}
                                onChange={(val) => {
                                    setFieldValue(`showWarningMessage.devTool`, val);
                                    markAsChanged();
                                }}
                            />
                            {values.showWarningMessage?.devTool && (
                                <TextField
                                    label="Message for devTool"
                                    value={values.warningMessages.devTool}
                                    onChange={(val) => {
                                        setFieldValue(`warningMessages.devTool`, val);
                                        markAsChanged();
                                    }}
                                    multiline={4}
                                    autoComplete="off"
                                    helpText="This message will be displayed when users attempt to open devtools"
                                />
                            )}
                        </BlockStack>
                    </Card>
                </Grid.Cell>
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                    <Card>
                        <InlineStack align="space-between" blockAlign="start">
                            <InlineStack gap="200">
                                <img src="https://cdn.shopify.com/s/files/1/0964/8878/4171/files/printNew.png?v=1766824476" alt="CTRL + P Protection" style={{ width: '50px', height: '50px', objectFit: 'contain', borderRadius: '8px' }} />
                                <BlockStack gap="200">
                                    <InlineStack align="start" blockAlign="center" gap="200">
                                        <Text fontWeight="semibold">Print Screen Protection (CTRL + P)</Text>
                                        <Badge tone={values.keyboardProtection.ctrl_p ? "success" : "critical"}>
                                            {values.keyboardProtection.ctrl_p ? "Activated" : "Deactivated"}
                                        </Badge>
                                    </InlineStack>
                                    <Text tone="subdued" variant="bodySm" style={{ maxWidth: '160px', wordWrap: 'break-word', lineHeight: '1.4' }}>
                                        Prevents users from printing using keyboard <br /> shortcuts.
                                    </Text>
                                </BlockStack>
                            </InlineStack>
                            <Button
                                tone={values.keyboardProtection.ctrl_p ? "critical" : "success"}
                                onClick={() => handleKeyboardToggle("ctrl_p")}
                            >
                                {values.keyboardProtection.ctrl_p ? "Disable" : "Enable"}
                            </Button>
                        </InlineStack>
                        <Placeholder height="10px" />
                        <BlockStack gap="400">
                            <Checkbox
                                label="Warning Message"
                                checked={values.showWarningMessage?.ctrl_p || false}
                                onChange={(val) => {
                                    setFieldValue(`showWarningMessage.ctrl_p`, val);
                                    markAsChanged();
                                }}
                            />
                            {values.showWarningMessage?.ctrl_p && (
                                <TextField
                                    label="Message for CTRL + P"
                                    value={values.warningMessages.ctrl_p || ""}
                                    onChange={(val) => {
                                        setFieldValue(`warningMessages.ctrl_p`, val);
                                        markAsChanged();
                                    }}
                                    multiline={4}
                                    autoComplete="off"
                                    helpText="This message will be displayed when users attempt to use CTRL + P"
                                />
                            )}
                        </BlockStack>
                        </Card>
                </Grid.Cell>
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                        <Card>
                            <InlineStack align="space-between" blockAlign="center">
                                <InlineStack gap="300" blockAlign="center">
                                <img src="https://cdn.shopify.com/s/files/1/0964/8878/4171/files/saveNew.png?v=1766824476" alt="CTRL + S Protection" style={{ width: '50px', height: '50px', objectFit: 'contain', borderRadius: '8px' }} />
                                <BlockStack gap="200">
                                    <InlineStack align="start" blockAlign="center" gap="200">
                                        <Text fontWeight="semibold">Save Protection (CTRL + S)</Text>
                                        <Badge tone={values.keyboardProtection.ctrl_s ? "success" : "critical"}>
                                            {values.keyboardProtection.ctrl_s ? "Activated" : "Deactivated"}
                                            </Badge>
                                        </InlineStack>
                                    <Text tone="subdued" variant="bodySm" style={{ maxWidth: '160px', wordWrap: 'break-word', lineHeight: '1.4' }}>
                                        Stops users from saving the webpage locally from <br /> their browser
                                    </Text>
                                    </BlockStack>
                                </InlineStack>
                                <Button
                                tone={values.keyboardProtection.ctrl_s ? "critical" : "success"}
                                onClick={() => handleKeyboardToggle("ctrl_s")}
                                >
                                {values.keyboardProtection.ctrl_s ? "Disable" : "Enable"}
                                </Button>
                        </InlineStack>
                        <Placeholder height="10px" />
                        <BlockStack gap="400">
                            <Checkbox
                                label="Warning Message"
                                checked={values.showWarningMessage?.ctrl_s || false}
                                onChange={(val) => {
                                    setFieldValue(`showWarningMessage.ctrl_s`, val);
                                    markAsChanged();
                                }}
                            />
                            {values.showWarningMessage?.ctrl_s && (
                                <TextField
                                    label="Message for CTRL + S"
                                    value={values.warningMessages.ctrl_s || ""}
                                    onChange={(val) => {
                                        setFieldValue(`warningMessages.ctrl_s`, val);
                                        markAsChanged();
                                    }}
                                    multiline={4}
                                    autoComplete="off"
                                    helpText="This message will be displayed when users attempt to use CTRL + S"
                                />
                            )}
                        </BlockStack>
                    </Card>
                </Grid.Cell>
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                    <Card>
                        <InlineStack align="space-between" blockAlign="center">
                            <InlineStack gap="300" blockAlign="center">
                                <img src="https://cdn.shopify.com/s/files/1/0964/8878/4171/files/viewSourceNew.png?v=1766824476" alt="CTRL + U Protection" style={{ width: '50px', height: '50px', objectFit: 'contain', borderRadius: '8px' }} />
                                <BlockStack gap="200">
                                    <InlineStack align="start" blockAlign="center" gap="200">
                                        <Text fontWeight="semibold">View Source Protection (CTRL + U)</Text>
                                        <Badge tone={values.keyboardProtection.ctrl_u ? "success" : "critical"}>
                                            {values.keyboardProtection.ctrl_u ? "Activated" : "Deactivated"}
                                        </Badge>
                                    </InlineStack>
                                    <Text tone="subdued" variant="bodySm" style={{ maxWidth: '160px', wordWrap: 'break-word', lineHeight: '1.4' }}>
                                        Blocks access to the page's source code using <br /> keyboard shortcuts.
                                    </Text>
                                </BlockStack>
                            </InlineStack>
                            <Button
                                tone={values.keyboardProtection.ctrl_u ? "critical" : "success"}
                                onClick={() => handleKeyboardToggle("ctrl_u")}
                            >
                                {values.keyboardProtection.ctrl_u ? "Disable" : "Enable"}
                                    </Button>
                                </InlineStack>
                        <Placeholder height="10px" />
                        <BlockStack gap="400">
                                        <Checkbox
                                label="Warning Message"
                                checked={values.showWarningMessage?.ctrl_u || false}
                                onChange={(val) => {
                                    setFieldValue(`showWarningMessage.ctrl_u`, val);
                                    markAsChanged();
                                }}
                            />
                            {values.showWarningMessage?.ctrl_u && (
                                <TextField
                                    label="Message for CTRL + U"
                                    value={values.warningMessages.ctrl_u || ""}
                                    onChange={(val) => {
                                        setFieldValue(`warningMessages.ctrl_u`, val);
                                        markAsChanged();
                                    }}
                                    multiline={4}
                                    autoComplete="off"
                                    helpText="This message will be displayed when users attempt to use CTRL + U"
                                />
                                )}
                            </BlockStack>
                        </Card>
                </Grid.Cell>
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                    <Card>
                        <InlineStack align="space-between" blockAlign="center">
                            <InlineStack gap="300" blockAlign="center">
                                <img src="https://cdn.shopify.com/s/files/1/0964/8878/4171/files/TextSelectionNew.png?v=1766824476" alt="CTRL + A Protection" style={{ width: '50px', height: '50px', objectFit: 'contain', borderRadius: '8px' }} />
                                <BlockStack gap="200">
                                    <InlineStack align="start" blockAlign="center" gap="200">
                                        <Text fontWeight="semibold">Select All Protection (CTRL + A)</Text>
                                        <Badge tone={values.keyboardProtection.ctrl_a ? "success" : "critical"}>
                                            {values.keyboardProtection.ctrl_a ? "Activated" : "Deactivated"}
                                        </Badge>
                                    </InlineStack>
                                    <Text tone="subdued" variant="bodySm" style={{ maxWidth: '160px', wordWrap: 'break-word', lineHeight: '1.4' }}>
                                        Prevents visitors from selecting all text or elements <br /> at once.
                                    </Text>
                                </BlockStack>
                            </InlineStack>
                            <Button
                                tone={values.keyboardProtection.ctrl_a ? "critical" : "success"}
                                onClick={() => handleKeyboardToggle("ctrl_a")}
                            >
                                {values.keyboardProtection.ctrl_a ? "Disable" : "Enable"}
                            </Button>
                        </InlineStack>
                        <Placeholder height="10px" />
                        <BlockStack gap="400">
                            <Checkbox
                                label="Warning Message"
                                checked={values.showWarningMessage?.ctrl_a || false}
                                onChange={(val) => {
                                    setFieldValue(`showWarningMessage.ctrl_a`, val);
                                    markAsChanged();
                                }}
                            />
                            {values.showWarningMessage?.ctrl_a && (
                                <TextField
                                    label="Message for CTRL + A"
                                    value={values.warningMessages.ctrl_a || ""}
                                    onChange={(val) => {
                                        setFieldValue(`warningMessages.ctrl_a`, val);
                                        markAsChanged();
                                    }}
                                    multiline={4}
                                    autoComplete="off"
                                    helpText="This message will be displayed when users attempt to use CTRL + A"
                                />
                            )}
                        </BlockStack>
                    </Card>
                </Grid.Cell>
                <Grid.Cell columnSpan={{ xs: 6, sm: 3, md: 3, lg: 6, xl: 6 }}>
                    <Card>
                        <InlineStack align="space-between" blockAlign="center">
                            <InlineStack gap="300" blockAlign="center">
                                <img src="https://cdn.shopify.com/s/files/1/0964/8878/4171/files/curDisabledNew.png?v=1766824476" alt="CTRL + X Protection" style={{ width: '50px', height: '50px', objectFit: 'contain', borderRadius: '8px' }} />
                                <BlockStack gap="200">
                                    <InlineStack align="start" blockAlign="center" gap="200">
                                        <Text fontWeight="semibold">Cut Protection (CTRL + X)</Text>
                                        <Badge tone={values.keyboardProtection.ctrl_x ? "success" : "critical"}>
                                            {values.keyboardProtection.ctrl_x ? "Activated" : "Deactivated"}
                                        </Badge>
                                    </InlineStack>
                                    <Text tone="subdued" variant="bodySm" style={{ maxWidth: '160px', wordWrap: 'break-word', lineHeight: '1.4' }}>
                                        Stops users from cutting selected content using <br /> shortcuts.
                                    </Text>
                                </BlockStack>
                            </InlineStack>
                            <Button
                                tone={values.keyboardProtection.ctrl_x ? "critical" : "success"}
                                onClick={() => handleKeyboardToggle("ctrl_x")}
                            >
                                {values.keyboardProtection.ctrl_x ? "Disable" : "Enable"}
                            </Button>
                        </InlineStack>
                        <Placeholder height="10px" />
                        <BlockStack gap="400">
                            <Checkbox
                                label="Warning Message"
                                checked={values.showWarningMessage?.ctrl_x || false}
                                onChange={(val) => {
                                    setFieldValue(`showWarningMessage.ctrl_x`, val);
                                    markAsChanged();
                                }}
                            />
                            {values.showWarningMessage?.ctrl_x && (
                                <TextField
                                    label="Message for CTRL + X"
                                    value={values.warningMessages.ctrl_x || ""}
                                    onChange={(val) => {
                                        setFieldValue(`warningMessages.ctrl_x`, val);
                                        markAsChanged();
                                    }}
                                    multiline={4}
                                    autoComplete="off"
                                    helpText="This message will be displayed when users attempt to use CTRL + X"
                                />
                            )}
                        </BlockStack>
                    </Card>
                </Grid.Cell>
            </Grid>
            {toastMarkup}
            <Modal
                open={previewModalActive}
                onClose={() => setPreviewModalActive(false)}
                title="Warning Messages Preview"
            >
                <Modal.Section>
                    <BlockStack gap="400">
                        {values.showWarningMessage?.textCopy && (
                            <Card>
                                <BlockStack gap="200">
                                    <Text fontWeight="bold">Content Copy Protection</Text>
                                    <div style={{ padding: 15, background: "#FFF2C5", borderRadius: 8 }}>
                                        <Text>{values.warningMessages.textCopy}</Text>
                                    </div>
                                </BlockStack>
                            </Card>
                        )}
                        {values.showWarningMessage?.devTool && (
                            <Card>
                                <BlockStack gap="200">
                                    <Text fontWeight="bold">DevTools Protection</Text>
                                    <div style={{ padding: 15, background: "#FFF2C5", borderRadius: 8 }}>
                                        <Text>{values.warningMessages.devTool}</Text>
                                    </div>
                                </BlockStack>
                            </Card>
                        )}
                        {values.showWarningMessage?.rightClick && (
                            <Card>
                                <BlockStack gap="200">
                                    <Text fontWeight="bold">Right Click Protection</Text>
                                    <div style={{ padding: 15, background: "#FFF2C5", borderRadius: 8 }}>
                                        <Text>{values.warningMessages.rightClick}</Text>
                                    </div>
                                </BlockStack>
                            </Card>
                        )}
                        {values.showWarningMessage?.ctrl_p && (
                            <Card>
                                <BlockStack gap="200">
                                    <Text fontWeight="bold">CTRL + P (Print Screen)</Text>
                                    <div style={{ padding: 15, background: "#FFF2C5", borderRadius: 8 }}>
                                        <Text>{values.warningMessages.ctrl_p}</Text>
                                    </div>
                                </BlockStack>
                            </Card>
                        )}
                        {values.showWarningMessage?.ctrl_s && (
                            <Card>
                                <BlockStack gap="200">
                                    <Text fontWeight="bold">CTRL + S (Save)</Text>
                                    <div style={{ padding: 15, background: "#FFF2C5", borderRadius: 8 }}>
                                        <Text>{values.warningMessages.ctrl_s}</Text>
                                    </div>
                                </BlockStack>
                            </Card>
                        )}
                        {values.showWarningMessage?.ctrl_u && (
                            <Card>
                                <BlockStack gap="200">
                                    <Text fontWeight="bold">CTRL + U (View Source)</Text>
                                    <div style={{ padding: 15, background: "#FFF2C5", borderRadius: 8 }}>
                                        <Text>{values.warningMessages.ctrl_u}</Text>
                                    </div>
                                </BlockStack>
                            </Card>
                        )}
                        {values.showWarningMessage?.ctrl_a && (
                            <Card>
                                <BlockStack gap="200">
                                    <Text fontWeight="bold">CTRL + A (Select All)</Text>
                                    <div style={{ padding: 15, background: "#FFF2C5", borderRadius: 8 }}>
                                        <Text>{values.warningMessages.ctrl_a}</Text>
                                    </div>
                                </BlockStack>
                            </Card>
                        )}
                        {values.showWarningMessage?.ctrl_x && (
                            <Card>
                                <BlockStack gap="200">
                                    <Text fontWeight="bold">CTRL + X (Cut)</Text>
                            <div style={{ padding: 15, background: "#FFF2C5", borderRadius: 8 }}>
                                        <Text>{values.warningMessages.ctrl_x}</Text>
                            </div>
                                </BlockStack>
                            </Card>
                        )}
                        {!values.showWarningMessage?.textCopy &&
                            !values.showWarningMessage?.devTool &&
                            !values.showWarningMessage?.rightClick &&
                            !values.showWarningMessage?.ctrl_p &&
                            !values.showWarningMessage?.ctrl_s &&
                            !values.showWarningMessage?.ctrl_u &&
                            !values.showWarningMessage?.ctrl_a &&
                            !values.showWarningMessage?.ctrl_x && (
                                <Card>
                                    <Text tone="subdued">No warning messages enabled. Enable warning messages to see preview.</Text>
                        </Card>
                            )}
                    </BlockStack>
                </Modal.Section>
            </Modal>
            {hasUnsavedChanges && <ContextSaveBar
                message="You have unsaved changes"
                saveAction={{
                    content: "Save",
                    onAction: handleSave,
                    loading: isCreateSettingLoading,
                    disabled: isCreateSettingLoading
                }}
                discardAction={{
                    content: "Discard",
                    onAction: handleDiscard,
                    disabled: isCreateSettingLoading
                }}
                visible={hasUnsavedChanges}
            />}
            <br />
        </Page>
    );
};

export default SettingsPage;